import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import type { CustomerDto } from './types';
import { useAdjustPoints } from './customersApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  customer: CustomerDto | null;
}

export function AdjustPointsModal({ opened, onClose, customer }: Props) {
  const { t } = useTranslation();
  const adjust = useAdjustPoints();
  const [delta, setDelta] = useState<number | ''>(0);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) {
      setDelta(0);
      setNote('');
      setError(null);
    }
  }, [opened]);

  if (!customer) return null;

  const numericDelta = typeof delta === 'number' ? delta : 0;
  const valid = numericDelta !== 0 && note.trim().length > 0;
  const newBalance = customer.pointsBalance + numericDelta;

  const submit = async () => {
    setError(null);
    try {
      await adjust.mutateAsync({
        id: customer.customerId,
        request: { delta: numericDelta, note: note.trim() },
      });
      notifications.show({
        color: 'green',
        title: t('customers.points.successTitle'),
        message: `${customer.fullName} · ${newBalance}`,
      });
      onClose();
    } catch (err) {
      const msg = isAxiosError(err)
        ? ((err.response?.data as { detail?: string; title?: string } | undefined)?.detail ??
           (err.response?.data as { title?: string } | undefined)?.title ??
           err.message)
        : t('common.toasts.errorBody');
      setError(msg);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600}>
          {t('customers.points.title')} — {customer.fullName}
        </Text>
      }
      size="sm"
      centered
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {t('customers.points.currentBalance')}
          </Text>
          <Text size="lg" fw={700} className="font-numeric">
            {customer.pointsBalance}
          </Text>
        </Group>

        <NumberInput
          label={t('customers.points.deltaLabel')}
          description={t('customers.points.deltaHelp')}
          value={delta}
          onChange={(v) => setDelta(typeof v === 'string' ? Number(v) : v)}
          step={10}
          required
        />

        <Textarea
          label={t('customers.points.noteLabel')}
          description={t('customers.points.noteHelp')}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          minRows={2}
          required
        />

        {numericDelta !== 0 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {t('customers.points.newBalance')}
            </Text>
            <Text
              size="md"
              fw={600}
              c={newBalance < 0 ? 'red' : 'var(--wheels-ink-strong)'}
              className="font-numeric"
            >
              {newBalance}
            </Text>
          </Group>
        )}

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} stroke={1.6} />}>
            {error}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={adjust.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} loading={adjust.isPending} disabled={!valid}>
            {t('customers.points.apply')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
